import { useMemo, useState } from 'react';
import { NotificationDialog } from '../../../CommonFunctions/DialogComponents';

import { IField } from './EvaluationInterface';
import { getCalculationFlow, isValidEvaluation } from '../../utils';

import LeftOperandHeader from './LeftOperandHeader';
import TimeGrain from './TimeGrain';
import Picklist from './Picklist';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../redux';
import RelativeFilter from './RelativeFilter';
import SearchCondition from './SearchCondition';
import {
  equalPatternCollections,
  withPatternCollections,
} from '../../../FilterUtils/FilterPatternCollection';
import { ICondition } from '../../../../redux/Calculations/CurrentCalculationSessionInterface';
import { AlertColor } from '@mui/material';

const EvaluationCard = ({
  evaluation,
  evaluationType,
  propKey,
  filterIdx,
  conditionFilterId,
  disableUpdate,
}: {
  propKey: string;
  evaluation: ICondition;
  evaluationType: string;
  filterIdx: number;
  lastDroppedEvaluationUid: string | null;
  conditionFilterId: string;
  disableUpdate?: boolean;
}) => {
  /**
   * at this point currentCalculationSession cannot be null
   */
  const _currentCalculationSession = useSelector(
    (state: RootState) => state.calculations.properties[propKey].currentCalculationSession!,
  );
  const activeFlow = _currentCalculationSession.activeFlow;
  const activeConditonIndex = _currentCalculationSession.activeCondition;

  const conditionTypeElse =
    _currentCalculationSession.calculationInfo.flows[activeFlow!][activeConditonIndex!]
      .condition === 'Else';

  const [collapse, setCollapse] = useState<boolean>(false);

  const evaluationSourceDetailsForField: IField =
    _currentCalculationSession.calculationInfo.fields[evaluation.leftOperand[0] as string];

  const fieldDetails = useMemo(
    () =>
      evaluationType === 'field'
        ? _currentCalculationSession.calculationInfo.fields[evaluation.leftOperand[0] as string]
        : null,
    [evaluationType],
  );
  const calculationDetails = useMemo(
    () =>
      evaluationType === 'flow'
        ? getCalculationFlow(
            evaluation.leftOperand[0] as string,
            evaluationType,
            _currentCalculationSession,
          )
        : null,
    [evaluationType],
  );

  const leftOperandType = useMemo(() => {
    if (evaluation.leftOperandType[0] === 'field')
      return _currentCalculationSession.calculationInfo.fields[evaluation.leftOperand[0]!].dataType;
    else {
      const flowName =
        _currentCalculationSession.calculationInfo.flows[evaluation?.leftOperand[0] as string][0]
          .flow;
      const dataType =
        flowName === 'IfElse'
          ? _currentCalculationSession.calculationInfo.selectedResultType[
              evaluation.leftOperand[0] as string
            ]
          : _currentCalculationSession?.calculationInfo?.flows[
              evaluation.leftOperand[0] as string
            ][0]?.resultantDatatype;

      return dataType;
    }
  }, []);
  const isValid = useMemo(() => {
    if (!_currentCalculationSession) return false;
    return isValidEvaluation(evaluation, leftOperandType!);
  }, [_currentCalculationSession, evaluation, leftOperandType]);
  const displayName =
    evaluationType === 'flow'
      ? _currentCalculationSession.flowDisplayNames[
          evaluation.leftOperand[0]! /**leftOperand cannot be null */
        ]
      : evaluationSourceDetailsForField.displayName;
  const flowName =
    _currentCalculationSession.calculationInfo.flows[evaluation?.leftOperand[0] as string]?.[0]
      ?.flow;
  const dataType = useMemo(
    () =>
      evaluationType === 'flow'
        ? flowName === 'IfElse'
          ? _currentCalculationSession?.calculationInfo.selectedResultType[
              evaluation?.leftOperand[0] as string
            ]
          : _currentCalculationSession.calculationInfo.flows[
              evaluation?.leftOperand[0] as string
            ][0].resultantDatatype
        : evaluationSourceDetailsForField.dataType,
    [evaluationType],
  );
  const [alert, setAlert] = useState<{
    message: string;
    severity: AlertColor;
  } | null>(null);

  const getOperatorPattern = () => {
    if (evaluationType === 'field' && fieldDetails) {
      switch (fieldDetails.dataType) {
        case 'decimal':
        case 'integer':
          return equalPatternCollections;
        case 'text':
          return withPatternCollections;
        case 'timestamp':
        case 'date':
          return equalPatternCollections;
      }
    } else if (evaluationType === 'flow') {
      let calculationDataType;

      if (
        _currentCalculationSession.calculationInfo.flows[evaluation?.leftOperand[0] as string][0]
          .filter
      ) {
        const dataTypeObjForIfElse = _currentCalculationSession.calculationInfo.selectedResultType;
        calculationDataType = dataTypeObjForIfElse[evaluation?.leftOperand[0] as string];
      } else {
        calculationDataType =
          _currentCalculationSession.calculationInfo.flows[evaluation?.leftOperand[0] as string][0]
            .resultantDatatype;
      }

      switch (calculationDataType) {
        case 'decimal':
        case 'integer':
          return equalPatternCollections;
        case 'text':
          return withPatternCollections;
        case 'timestamp':
        case 'date':
          return equalPatternCollections;
      }
    }
    return withPatternCollections;
  };

  return conditionTypeElse ? (
    'Else'
  ) : (
    <div
      className="UserFilterCard"
      style={{
        marginBottom: '15px',
      }}
    >
      {alert && (
        <NotificationDialog
          severity={alert.severity}
          openAlert={!!alert}
          onCloseAlert={() => setAlert(null)}
          testMessage={alert.message}
        />
      )}
      <LeftOperandHeader
        operandIndex={filterIdx}
        activeFlow={activeFlow!}
        propKey={propKey}
        leftOperandName={displayName}
        onExpandCollapse={(value) => setCollapse(value)}
        filterType={evaluation.filterType}
        shouldExclude={evaluation.shouldExclude}
        dataType={dataType!}
        showWarning={!isValid}
        borderColor={isValid ? '#af99db' : 'red'}
        conditionFilterId={conditionFilterId}
        disabled={disableUpdate}
      />
      {!collapse ? (
        <div
          className="UserSelectionDiv"
          style={{
            marginTop: 0,
            rowGap: 0,
          }}
        >
          {(dataType === 'date' || dataType === 'timestamp') &&
          evaluation.filterType !== 'Relative Filter' ? (
            <TimeGrain
              timeGrain={evaluation.timeGrain}
              propKey={propKey}
              conditionFilterId={conditionFilterId}
              filterIdx={filterIdx}
              filterType={evaluation.filterType}
              disabled={disableUpdate}
            />
          ) : null}
          {evaluation.filterType === 'Pick List' ? (
            <Picklist
              disabled={disableUpdate}
              propKey={propKey}
              conditionFilterID={conditionFilterId}
              filterIdx={filterIdx}
              calculationDetails={calculationDetails}
              fieldDetails={fieldDetails}
              timeGrain={evaluation.timeGrain}
              flowId={evaluationType === 'flow' ? (evaluation.leftOperand[0] as string) : undefined}
              userSelection={evaluation.rightOperand!}
              shouldExclude={evaluation.shouldExclude}
              dataType={dataType}
            />
          ) : evaluation.filterType === 'Relative Filter' ? (
            <RelativeFilter
              propKey={propKey}
              conditionFilterId={conditionFilterId}
              filterIdx={filterIdx}
              fieldDetails={fieldDetails}
              calculationDetails={calculationDetails}
              relativeCondition={evaluation.relativeCondition}
              isFlow={evaluationType === 'flow'}
              disabled={disableUpdate}
              dataType={dataType}
            />
          ) : (
            <SearchCondition
              propKey={propKey}
              conditionFilterId={conditionFilterId}
              filterIdx={filterIdx}
              rightOperand={evaluation.rightOperand}
              rightOperandType={evaluation.rightOperandType as string[]}
              operator={evaluation.operator}
              disabled={disableUpdate}
              operatorPattern={getOperatorPattern()}
              leftOperandtype={leftOperandType as string}
              timeGrain={evaluation.timeGrain}
            />
          )}
        </div>
      ) : null}
    </div>
  );
};

export default EvaluationCard;
